import axios, { AxiosError, ResponseType } from 'axios';
import { HttpException, UnauthorizedException } from './exceptions';

/**
 * axios's own default `timeout` is 0 — none — so a request that completes the TCP
 * handshake and then never answers neither resolves nor rejects (SessionService.ts's
 * `VERIFY_SESSION_TIMEOUT_MS` comment names the same failure mode for `accounts/me`,
 * which is why THAT call already carries its own 8s AbortSignal). Every other call
 * through this class had no bound at all until now.
 *
 * That mattered beyond a single hung request once DiagnosisService.refresh() grew a
 * per-study in-flight guard: the guard clears in a `finally`, but a `finally` only
 * runs once the awaited call settles — an unbounded hang would hold the guard for the
 * rest of the component's life, silently disabling that study's polling with no error
 * and no recovery short of a remount. A blanket default here is what guarantees
 * settlement for every caller, not just DiagnosisService's.
 *
 * Checked every existing caller for a behaviour change:
 *   - authHttp.get('accounts/me', { signal: verifySessionTimeoutSignal() }) — already
 *     bounded at 8s by its own AbortSignal, which fires well before this 15s default;
 *     no observable change.
 *   - authHttp.post('auth/log-out', {}) — previously unbounded. logOut()'s `finally`
 *     (setAccount(null), redirect to the login URL) could never run if this hung; now it
 *     runs within 15s. Strictly an improvement, not a new failure path — logOut()
 *     already swallows the error this produces.
 *   - viewerHttp's five callers (trigger, revoke, status, assessments get/post) —
 *     previously unbounded, each already reached by a try/catch in DiagnosisService or
 *     ReportAssessmentService that treats any network error the same way. A timeout is
 *     just another AxiosError with no `response`, mapped to HttpException(0, ...) by
 *     toException() below exactly like a dropped connection is today.
 * No caller currently sets its own `timeout`, and none needs to opt out — a per-call
 * override can be added to RequestOptions the day one does.
 */
const DEFAULT_TIMEOUT_MS = 15000;

export interface RequestOptions {
  headers?: Record<string, string>;
  params?: Record<string, unknown>;
  responseType?: ResponseType;
  withCredentials?: boolean;
  signal?: AbortSignal;
  /**
   * Per-call override of DEFAULT_TIMEOUT_MS. Pre-authorized by this file's own
   * comment at :31-32 — "a per-call override can be added to RequestOptions the day
   * one does" — and M4.3 is that day: uploads need develop's five-minute budget
   * (DicomUploader.ts:32), where 15 s would abort a large instance on a slow link.
   */
  timeout?: number;
  /**
   * Normalized, so no caller ever sees an AxiosProgressEvent (D11). `total` is 0
   * when axios cannot determine it; the caller substitutes its own known size.
   */
  onUploadProgress?: (loaded: number, total: number) => void;
}

export interface HttpResponse<T> {
  data: T;
  /**
   * The real response status. Additive, and added for one concrete reason: a
   * caller that receives a 2xx whose BODY is wrong (SessionService's account
   * validation) has to raise an HttpException, and without this it would have to
   * invent the status — either `0`, which this file reserves for "no response at
   * all", or a hardcoded `200` that lies about a 204. Both make the one-line boot
   * diagnostic ambiguous, which is the thing that diagnostic exists to avoid.
   */
  status: number;
}

/**
 * Providers, not values (design D3). Both are invoked PER REQUEST.
 *
 * Every base-URL bug on `develop` is a construction-time capture:
 * PACSApiClient.ts:4 captures it in a default parameter, DicomUploader.ts:27 in
 * a field initializer, and four call sites do `new HttpClient(getViewerUrl())`.
 * With providers that class of bug is unreachable.
 *
 * For `getAuthHeader` this is REQUIRED, not defensive (D9): the `accessToken`
 * cookie changes mid-session, and §5.5 establishes there is no refresh endpoint
 * — so there is no moment at which the viewer LEARNS the token changed. Reading
 * it per request is the only mechanism that keeps up.
 *
 * `TBaseUrl` exists for exactly one caller: orthancHttp's `getBaseUrl` is
 * `tryGetPacsRoot`, `string | undefined`, because a passive readout must not call
 * `fatal()` (config/roots.ts, D-3) the way authHttp's `getAuthRoot` and viewerHttp's
 * `getViewerRoot` do. Generic rather than widening the field to `string | undefined`
 * outright: that would type EVERY provider's `getBaseUrl` as possibly-undefined,
 * including authHttp's and viewerHttp's, which never are. Each `HttpClient` instance
 * infers its own `TBaseUrl` from the provider object passed to its constructor, so the
 * other two stay exactly `() => string`.
 */
export interface HttpClientProviders<TBaseUrl extends string | undefined = string> {
  getBaseUrl: () => TBaseUrl;
  getAuthHeader?: () => Record<string, string> | undefined;
  /**
   * Invoked when a request maps to UnauthorizedException, before it is thrown — so a
   * caller's own catch still sees the error and decides what else to do. Optional
   * because authHttp deliberately does not set it: verifySession's 401 is already
   * routed by index.tsx's own catch, and logOut() POSTs through authHttp, so a handler
   * there would re-enter the logout it is reacting to.
   */
  onUnauthorized?: () => void;
}

type InternalOptions = RequestOptions & { method: string; data?: unknown };

/**
 * §6 defect 12. develop set `Content-Type: application/dicom` on a FormData body
 * (DicomUploader.ts:103) and axios silently replaced it — the alternative that does
 * not work, worth naming so it is not re-attempted. axios sets
 * `multipart/form-data` WITH ITS BOUNDARY only when it finds no Content-Type of its
 * own, so the JSON default below must be SKIPPED rather than overridden: a
 * multipart body with no boundary parameter is unparseable at the far end.
 */
function isFormData(data: unknown): boolean {
  return typeof FormData !== 'undefined' && data instanceof FormData;
}

export class HttpClient<TBaseUrl extends string | undefined = string> {
  constructor(private readonly providers: HttpClientProviders<TBaseUrl>) {}

  /**
   * Design defect 4: develop did `${this.baseUrl}/${endpoint}`, and 5 of the 6
   * Orthanc endpoints it called already began with '/', producing '//'.
   *
   * The `!baseUrl` guard below already treats `undefined` and `''` identically, so
   * `TBaseUrl` being `string | undefined` for orthancHttp needs no branch of its own
   * here: an absent PACS root throws this plain Error exactly like an empty one would,
   * and describeError's `status ?? 0` bucket (utils/describeError.ts) already covers
   * both this guard and fatal()'s own Error the same way.
   */
  private buildUrl(endpoint: string): string {
    const baseUrl = this.providers.getBaseUrl();

    if (!baseUrl) {
      throw new Error(`[sycai] HttpClient has no base URL for "${endpoint}"`);
    }

    return `${baseUrl.replace(/\/+$/, '')}/${endpoint.replace(/^\/+/, '')}`;
  }

  private async request<T>(endpoint: string, options: InternalOptions): Promise<HttpResponse<T>> {
    const {
      method,
      data,
      headers,
      params,
      responseType,
      // Design defect 6: develop hardcoded `true` and ignored the RequestOptions
      // field. The default stays true — the API is cookie-authenticated — but it
      // is now overridable.
      withCredentials = true,
      signal,
      timeout,
      onUploadProgress,
    } = options;

    // buildUrl() resolves the base URL via the `getBaseUrl` provider
    // (authHttp's is getAuthRoot), which can call fatal() -- a configuration
    // failure, not a transport one. Resolving it BEFORE the try keeps that
    // failure out of the catch below: staying inside it, the plain Error that
    // either fatal() or buildUrl's own empty-baseUrl guard throws would fall
    // into `!response` in toException() and come back as HttpException(0,
    // error.message) -- indistinguishable from a dead socket.
    const url = this.buildUrl(endpoint);

    try {
      const response = await axios({
        url,
        method,
        // Design defect 5: develop pre-JSON.stringify'd the body in post/put/
        // patch and then handed the string to axios, which serialized it again.
        data,
        params,
        responseType,
        withCredentials,
        signal,
        timeout: timeout ?? DEFAULT_TIMEOUT_MS,
        onUploadProgress: onUploadProgress
          ? event => onUploadProgress(event.loaded, event.total ?? 0)
          : undefined,
        headers: {
          // Only when there is a body. A Content-Type on a GET buys nothing and
          // turns an otherwise-simple cross-origin request into a preflighted one.
          ...(data === undefined || isFormData(data) ? {} : { 'Content-Type': 'application/json' }),
          ...(this.providers.getAuthHeader?.() ?? {}),
          ...headers,
        },
      });

      return { data: response.data, status: response.status };
    } catch (error) {
      const exception = this.toException(error as AxiosError);

      if (exception instanceof UnauthorizedException) {
        this.providers.onUnauthorized?.();
      }

      throw exception;
    }
  }

  /**
   * Deliberately does NOT call `errorHandler.getHTTPErrorHandler()` (§5.5).
   * develop's version did, conflating Cornerstone's image-load failures with our
   * REST calls — which is why it had to rewrite appInit.js.
   * `appConfig.httpErrorHandler` stays for Cornerstone's path, where it is
   * mandatory: init.tsx:318-324 invokes the result unguarded.
   */
  private toException(error: AxiosError): HttpException {
    const response = error.response;

    // Design defect 2: develop destructured `error.response.data` unguarded, so
    // any network / timeout / cancel error threw a TypeError from INSIDE the
    // error handler, replacing the real failure with a bogus one.
    if (!response) {
      return new HttpException(0, error.message || 'Network error');
    }

    const body = response.data;
    const message =
      (body && typeof body === 'object' && typeof (body as { message?: unknown }).message === 'string'
        ? (body as { message: string }).message
        : undefined) ??
      error.message ??
      'Unknown error occurred';

    if (response.status === 401 || UnauthorizedException.is(body)) {
      return new UnauthorizedException(message);
    }

    return new HttpException(response.status, message);
  }

  public get<T>(endpoint: string, options: RequestOptions = {}): Promise<HttpResponse<T>> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  public post<T>(endpoint: string, data?: unknown, options: RequestOptions = {}) {
    return this.request<T>(endpoint, { ...options, method: 'POST', data });
  }

  public put<T>(endpoint: string, data?: unknown, options: RequestOptions = {}) {
    return this.request<T>(endpoint, { ...options, method: 'PUT', data });
  }

  public patch<T>(endpoint: string, data?: unknown, options: RequestOptions = {}) {
    return this.request<T>(endpoint, { ...options, method: 'PATCH', data });
  }

  public delete<T>(endpoint: string, options: RequestOptions = {}): Promise<HttpResponse<T>> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }
}
